import React, { useState } from 'react';
import { AmountInput, TooltipLabel, CheckboxWithLabel } from './mileage-reimbursement-ui';
import { Plus, Tooltip } from './images/svg/icons';
import { useTranslation } from 'react-i18next';
import GenericTooltip from './generictooltip';
import FormField from './form-field';
import { InvoiceAdvancedCalculatorProps } from '../types/salary-calculator.types';
import AllowanceSection from './AllowanceSection';
import RotRutDeductionSection from './RotRutDeductionSection';
import { PENSION_OPTIONS, HOLIDAY_PAY, ALLEXPENSESDATA, handleToggle } from './../helper/salary.helpers';
import { } from '../components/images/svg/icons'

type CustomExpenseType = 'mileage' | 'travel' | 'materials' | 'allowance_sweden' | 'allowance_outside_sweden';
interface CustomExpense {
    id: number;
    type: CustomExpenseType;
    amount: number;
    articleNumber?: string;
    workingDescription?: string;
    vat?: number | string | null;
    isTaxFreeArticle?: boolean;
    isArticleShownInInvoice?: boolean;
    formData?: any;
}

const InvoiceAdvancedCalculator: React.FC<InvoiceAdvancedCalculatorProps & {
    customExpenses: CustomExpense[];
    handleAddCustomExpense: (type: CustomExpenseType) => void;
    handleCustomExpenseChange: (id: number, field: string, value: any) => void;
    handleRemoveCustomExpense: (id: number) => void;
    selectedTax?: string;
    handleTaxChange?: (tax: string) => void;
}> = ({
    currentAmount,
    activeTooltipId = null,
    setActiveTooltipId,
    name,
    selectedVat,
    selectedTax = 30,
    selectedPension,
    selectedHolidayPay,
    currency,
    handleVatChange,
    handleTaxChange,
    VAT_OPTIONS,
    handleHolidaypay,
    handleMileageAmount,
    mileageAmount,
    handleRotRutToggle,
    isRutDeductionApplied,
    isRotDeductionApplied,
    setIsMileageTaxFree,
    isMileageTaxFree,
    showMileageOnInvoice,
    setShowMileageOnInvoice,
    travelExpenseAmount,
    setTravelExpenseAmount,
    travelVat,
    setTravelVat,
    materialsAndToolsAmount,
    setMaterialsAndToolsAmount,
    setMaterialsToolsVat,
    materialsToolsVat,
    allowanceOutsideSweden,
    setAllowanceOutsideSweden,
    isAllowanceOutsideSwedenTaxFree,
    setIsAllowanceOutsideSwedenTaxFree,
    swedenAllowance,
    setSwedenAllowance,
    isMaterialsToolsTaxFree,
    setIsMaterialsToolsTaxFree,
    showMaterialsAndToolsOnInvoice,
    setShowMaterialsAndToolsOnInvoice,
    showSwedenAllowance,
    setShowSwedenAllowance,
    showForeignAllowance,
    setShowForeignAllowance,
    setIsSwedenAllowanceTaxFree,
    isSwedenAllowanceTaxFree,
    showTravelExpensesOnInvoice,
    setShowTravelExpensesOnInvoice,
    customExpenses = [],
    handleAddCustomExpense,
    handleCustomExpenseChange,
    handleRemoveCustomExpense,
    handlePensionChange,
}) => {
        const { t } = useTranslation();
        const [isDropdownOpen, setIsDropdownOpen] = useState(false);
        const isMobile = window.innerWidth < 768;

        return (
            <div>
                <span className="font-weight-bold">{t('VAT_AND_SAVINGS')}</span>
                <div className="my-4">
                    <FormField
                        type="select"
                        label={t('VAT')}
                        name="vat"
                        value={selectedVat || 'Select VAT'}
                        activeTooltipId={activeTooltipId}
                        setActiveTooltipId={setActiveTooltipId}
                        onChange={e => handleVatChange(e.target.value)}
                        options={VAT_OPTIONS.map(v => ({ label: `${v} %`, value: v }))}
                        isNote={false}
                        tooltipLabel="VALUE_ADDED_TAX_ON_INVOICE"
                    />
                    <div className='my-4'>
                        <div className="form-group">
                            <div>
                                <label className="form-label">{t('TAX')}</label>
                                <img src={Tooltip} className="tooltip-svg cursor-pointer" onClick={() => handleToggle(setActiveTooltipId, 'tax-advanced')} alt="" />
                                <GenericTooltip
                                    isActive={activeTooltipId === 'tax-advanced'}
                                    content={t('TAX_ON_INVOICE')}
                                    onClose={() => setActiveTooltipId(null)}
                                    width="w-60"
                                    position="left-0"
                                    zIndex="z-index-9"
                                />
                            </div>
                            <div className="d-flex rounded align-items-center mt-2" style={{ border: '1px solid #EAEBF0', borderRadius: '6px' }}>
                                <input
                                    type="text"
                                    className="form-control border-0 shadow-none"
                                    name="tax"
                                    value={selectedTax}
                                    onChange={handleTaxChange}
                                    style={{ flex: 1 }}
                                />
                                <div className="vr" />
                                <span className="px-3" style={{ color: 'rgba(24, 29, 39, 1)', opacity: 0.5 }}>%</span>
                            </div>
                        </div>
                    </div>
                    <div className='my-4'>

                        <FormField
                            type="select"
                            label={t('PENSION')}
                            name="pension"
                            currency={currency}
                            value={selectedPension}
                            activeTooltipId={activeTooltipId}
                            setActiveTooltipId={setActiveTooltipId}
                            placeholder='Select pension'
                            onChange={e => handlePensionChange(e.target.value)}
                            options={PENSION_OPTIONS.map((v: any) => ({ label: `${v}`, value: v }))}
                            NoteText={t('PENSION_NOTE')}
                            tooltipLabel="PENSION_INFORMATION_AND_SAVINGS"
                            disabled={currentAmount < 5000}
                        />

                    </div>
                    <FormField
                        type="select"
                        label={t('HOLIDAY_PAY')}
                        name="holidayPay"
                        value={selectedHolidayPay}
                        currency={currency}
                        placeholder='Select holiday pay'
                        activeTooltipId={activeTooltipId}
                        setActiveTooltipId={setActiveTooltipId}
                        onChange={e => handleHolidaypay(e.target.value)}
                        options={HOLIDAY_PAY.map(v => ({ label: `${v} %`, value: v }))}
                        NoteText={t('HOLIDAY_PAY_NOTE')}
                        tooltipLabel="HOLIDAY_PAY_ALLOCATION"
                    />
                </div>
                <div>
                    <h6 className="font-weight-bold my-4" style={{
                        color: 'rgba(24, 29, 39)',
                    }}>{t('EXPENSES_ENTRY_WITH_VAT_OPTION')}</h6>
                    <div className="form-group d-flex flex-column gap-3">
                        <div>
                            <TooltipLabel
                                label={t('MILEAGE_REIMBURSEMENT')}
                                tooltipId={name + '-mileage-main'}
                                tooltipActiveId={activeTooltipId}
                                setTooltipActiveId={setActiveTooltipId}
                                t={t}
                                tooltipLabel="MILEAGE_REIMBURSEMENT_PER_KILOMETER"
                            />
                            <div className="form-group border-0 d-flex flex-grow-1 mb-0">
                                <div className="d-flex rounded align-items-center">
                                    <div className="d-flex border-color-gray align-items-center justify-content-center w-100 position-relative" style={{ borderRadius: '6px', border: '1px solid #EAEBF0' }}>
                                        <input
                                            type="text"
                                            className="form-control border-0 p-0 px-2 shadow-none mb-0"
                                            placeholder="0"
                                            value={Number(mileageAmount)}
                                            onChange={e => handleMileageAmount(e.target.value)}
                                            style={{ flex: 1 }}
                                        />
                                        <div className="vr" />
                                        <span className="px-3" style={{
                                            color: 'rgba(24, 29, 39, 1)',
                                            opacity: 0.5
                                        }} >{currency}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-check ml-0 pl-0  custom-checkbox-wrapper mb-3">
                            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between w-100 gap-2 gap-md-0">
                                <div>
                                    <CheckboxWithLabel
                                        label={t('IS_MILEAGE_TAX_FREE')}
                                        tooltipId={name + '-mileage-taxfree-main'}
                                        tooltipActiveId={activeTooltipId}
                                        setTooltipActiveId={setActiveTooltipId}
                                        checked={isMileageTaxFree}
                                        onChange={() => setIsMileageTaxFree(!isMileageTaxFree)}
                                        tooltipLabel={t('IS_MILEAGE_TAX_FREE_TOOLTIP_DESCRIPTION')}
                                    />
                                </div>
                                <div>
                                    <CheckboxWithLabel
                                        label={t('SHOW_MILEAGE_ON_INVOICE')}
                                        tooltipId={name + '-mileage-showoninvoice-main'}
                                        tooltipActiveId={activeTooltipId}
                                        setTooltipActiveId={setActiveTooltipId}
                                        disabled={!isMileageTaxFree}
                                        checked={showMileageOnInvoice}
                                        onChange={() => setShowMileageOnInvoice(!showMileageOnInvoice)}
                                        tooltipLabel={t('SHOW_MILEAGE_ON_INVOICE_TOOLTIP_DESCRIPTION')}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-group border-0 d-flex mb-3 w-100">
                            <div className='d-flex justify-content-between gap-3 '>
                                <div className='d-flex flex-column gap-2 mb-0'>
                                    <div>
                                        <TooltipLabel
                                            label={t('TRAVEL_EXPENSES')}
                                            tooltipId={name + '-travel-expenses-main'}
                                            tooltipActiveId={activeTooltipId}
                                            setTooltipActiveId={setActiveTooltipId}
                                            t={t}
                                            tooltipLabel={t('TRAVEL_EXPENSES_TITLE')}
                                        />
                                    </div>
                                    <div className='d-flex gap-3'>
                                        <AmountInput
                                            // label={t('TRAVEL_EXPENSES')}
                                            value={travelExpenseAmount}
                                            currency={currency}
                                            onChange={(e) => setTravelExpenseAmount(Number(e.target.value))}
                                        />
                                    </div>
                                </div>

                                <div className="w-50">
                                    <FormField
                                        type="select"
                                        label={t('VAT')}
                                        name="travelvat"
                                        value={travelVat && travelVat !== '' ? travelVat : ''}
                                        isNote={false}
                                        tooltipId={name + '-travelvat'}
                                        currency={currency}
                                        activeTooltipId={activeTooltipId}
                                        setActiveTooltipId={setActiveTooltipId}
                                        onChange={e => setTravelVat(e.target.value)}
                                        options={VAT_OPTIONS.map(v => ({ label: `${v} %`, value: v }))}
                                        tooltipLabel="VALUE_ADDED_TAX_ON_INVOICE"
                                        placeholder="Select VAT"
                                    />
                                </div>
                            </div>
                            <div className='ml-0 mt-2 mt-md-0'>
                                <CheckboxWithLabel
                                    label={t('SHOW_TRAVEL_EXPENSES_ON_INVOICE')}
                                    tooltipId={name + '-showoninvoice'}
                                    tooltipActiveId={activeTooltipId}
                                    setTooltipActiveId={setActiveTooltipId}
                                    checked={!!showTravelExpensesOnInvoice}
                                    onChange={() => setShowTravelExpensesOnInvoice(!showTravelExpensesOnInvoice)}
                                    tooltipLabel={t('SHOW_TRAVEL_EXPENSES_ON_INVOICE_TOOLTIP_DESCRIPTION')}
                                />
                            </div>
                        </div>
                        <div className="form-group border-0 d-flex mb-3 w-100">
                            <div className='d-flex justify-content-between gap-3 '>
                                <div className='d-flex flex-column gap-2 mb-0'>
                                    <div>
                                        <TooltipLabel
                                            label={isMobile ? t('EXPENSES_FOR_MATERIALS_AND_TOOLS_MOBILE') : t('EXPENSES_FOR_MATERIALS_AND_TOOLS')}
                                            tooltipId={name + '-materials-tools-main'}
                                            tooltipActiveId={activeTooltipId}
                                            setTooltipActiveId={setActiveTooltipId}
                                            t={t}
                                            tooltipLabel="EXPENSES_FOR_MATERIALS_AND_TOOLS_TAXFREE"
                                        />
                                    </div>
                                    <div className='d-flex gap-3'>
                                        <AmountInput value={materialsAndToolsAmount} currency={currency} onChange={(e) => setMaterialsAndToolsAmount(Number(e.target.value))} />
                                    </div>
                                </div>

                                <div className="w-50">
                                    <FormField
                                        type="select"
                                        label={t('VAT')}
                                        name="materialsvat"
                                        value={materialsToolsVat && materialsToolsVat !== '' ? materialsToolsVat : ''}
                                        placeholder='Select VAT'
                                        tooltipId={name + '-materialstools'}
                                        activeTooltipId={activeTooltipId}
                                        setActiveTooltipId={setActiveTooltipId}
                                        isNote={false}
                                        options={VAT_OPTIONS.map(v => ({ label: `${v} %`, value: v }))}
                                        onChange={e => setMaterialsToolsVat(e.target.value)}
                                        tooltipLabel="VALUE_ADDED_TAX_ON_INVOICE"
                                    />
                                </div>
                            </div>
                            <div className='ml-0'>
                                <div className='d-flex flex-column flex-md-row justify-content-between gap-2 gap-md-0'>
                                    <div>
                                        <CheckboxWithLabel
                                            label={t('IS_MATERIALS_AND_TOOLS_TAX_FREE')}
                                            tooltipId={name + '-materials-taxfree-main'}
                                            tooltipActiveId={activeTooltipId}
                                            setTooltipActiveId={setActiveTooltipId}
                                            checked={isMaterialsToolsTaxFree}
                                            onChange={() => setIsMaterialsToolsTaxFree(!isMaterialsToolsTaxFree)}
                                            tooltipLabel={t('IS_MATERIALS_AND_TOOLS_TAX_FREE_TOOLTIP_DESC')}
                                        />
                                        <GenericTooltip
                                            isActive={false}
                                            content={t('WORK_RELATED_TRAVEL_EXPENSES_REIMBURSEMENT')}
                                            onClose={() => setActiveTooltipId(null)}
                                            width="w-50"
                                            position="left-0"
                                            zIndex="z-index-9"
                                        />
                                    </div>
                                    <div>
                                        <CheckboxWithLabel
                                            label={t('SHOW_MATERIALS_AND_TOOLS_ON_INVOICE')}
                                            tooltipId={name + '-materials-show-on-invoice-main'}
                                            tooltipActiveId={activeTooltipId}
                                            setTooltipActiveId={setActiveTooltipId}
                                            checked={showMaterialsAndToolsOnInvoice}
                                            disabled={!isMaterialsToolsTaxFree}
                                            onChange={() => setShowMaterialsAndToolsOnInvoice(!showMaterialsAndToolsOnInvoice)}
                                            tooltipLabel={t('SHOW_MATERIALS_AND_TOOLS_ON_INVOICE_TOOLTIP')} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <AllowanceSection
                                label={'ALLOWANCE_IN_SWEDEN'}
                                value={swedenAllowance}
                                onChange={(e) => setSwedenAllowance(Number(e.target.value))}
                                currency={currency}
                                tooltipActiveId={activeTooltipId}
                                setTooltipActiveId={setActiveTooltipId}
                                name={`${name}-sweden`}
                                checked={isSwedenAllowanceTaxFree}
                                tooltipLabel='ALLOWANCE_IN_SWEDEN_TAXFREE'

                            />
                            <div className='ml-0'>
                                <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between my-2 gap-2 gap-md-0'>
                                    <div>
                                        <CheckboxWithLabel
                                            label={t('IS_ALLOWANCE_IN_SWEDEN_TAX_FREE')}
                                            tooltipId={name + '-sweden-taxfree-main'}
                                            checked={isSwedenAllowanceTaxFree}
                                            onChange={() => setIsSwedenAllowanceTaxFree(!isSwedenAllowanceTaxFree)}
                                            tooltipActiveId={activeTooltipId}
                                            setTooltipActiveId={setActiveTooltipId}
                                            tooltipLabel={t('IS_ALLOWANCE_IN_SWEDEN_TAX_FREE_DESCRIPTION')}
                                        />
                                    </div>
                                    <div>
                                        <CheckboxWithLabel
                                            label={t('SHOW_ALLOWANCE_IN_SWEDEN_ON_INVOICE')}
                                            tooltipId={name + '-sweden-showoninvoice-main'}
                                            checked={showSwedenAllowance}
                                            onChange={() => setShowSwedenAllowance(!showSwedenAllowance)}
                                            tooltipActiveId={activeTooltipId}
                                            setTooltipActiveId={setActiveTooltipId}
                                            tooltipLabel={t('SHOW_ALLOWANCE_IN_SWEDEN_ON_INVOICE_TOOLTIP')}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <AllowanceSection
                                label={t('ALLOWANCE_OUTSIDE_SWEDEN')}
                                value={allowanceOutsideSweden}
                                onChange={(e) => setAllowanceOutsideSweden(Number(e.target.value))}
                                currency={currency}
                                tooltipActiveId={activeTooltipId}
                                setTooltipActiveId={setActiveTooltipId}
                                name={`${name}-outside-sweden`}
                                checked={isAllowanceOutsideSwedenTaxFree}
                                tooltipLabel={t('ALLOWANCE_OUTSIDE_SWEDEN_TAXFREE')}
                            />
                            <div className='ml-0 pl-0'>
                                <div className='d-flex align-items-between flex-column flex-md-row justify-content-between my-2'>
                                    <div>
                                        <CheckboxWithLabel
                                            label={t('IS_ALLOWANCE_OUTSIDE_SWEDEN_TAX_FREE')}
                                            tooltipId={name + '-outside-taxfree-main'}
                                            checked={isAllowanceOutsideSwedenTaxFree}
                                            onChange={() => setIsAllowanceOutsideSwedenTaxFree(!isAllowanceOutsideSwedenTaxFree)}
                                            tooltipActiveId={activeTooltipId}
                                            setTooltipActiveId={setActiveTooltipId}
                                            tooltipLabel={t("IS_ALLOWANCE_OUTSIDE_SWEDEN_TAX_FREE_DESCRIPTION")}
                                        />
                                    </div>
                                    <div>
                                        <CheckboxWithLabel
                                            label={t('SHOW_ALLOWANCE_OUTSIDE_SWEDEN_ON_INVOICE')}
                                            tooltipId={name + '-outside-showoninvoice-main'}
                                            checked={showForeignAllowance}
                                            onChange={() => setShowForeignAllowance(!showForeignAllowance)}
                                            tooltipActiveId={activeTooltipId}
                                            setTooltipActiveId={setActiveTooltipId}
                                            tooltipLabel={t("SHOW_ALLOWANCE_OUTSIDE_SWEDEN_ON_INVOICE_TOOLTIP")}
                                        />
                                    </div>
                                </div>
                            </div>

                        </div>

                    </div>
                    <RotRutDeductionSection
                        activeTooltipId={activeTooltipId}
                        setActiveTooltipId={setActiveTooltipId}
                        name={name}
                        isRutDeductionApplied={isRutDeductionApplied}
                        isRotDeductionApplied={isRotDeductionApplied}
                        handleRotRutToggle={handleRotRutToggle}
                    />
                </div>

                <div className="my-3">
                    {customExpenses.map((exp, idx) => {
                        switch (exp.type) {
                            case 'mileage':
                                return (
                                    <div className='my-4' key={exp.id}>
                                        <div >
                                            <div className="form-group border-0 d-flex flex-grow-1 mb-3">
                                                <label htmlFor="">{t('MILEAGE_REIMBURSEMENT')}</label>
                                                <div className="d-flex rounded align-items-center">
                                                    <div className="d-flex border-color-gray align-items-center justify-content-center w-100 position-relative" style={{ borderRadius: '6px', border: '1px solid #EAEBF0' }}>
                                                        <input
                                                            type="text"
                                                            className="form-control border-0 p-0 px-2 shadow-none mb-0"
                                                            placeholder="0"
                                                            value={exp.amount}
                                                            onChange={e => handleCustomExpenseChange(exp.id, 'amount', Number(e.target.value))}
                                                            style={{ flex: 1 }}
                                                        />
                                                        <div className="vr" />
                                                        <span className="px-3" style={{
                                                            opacity: 0.5
                                                        }}>{currency}</span>

                                                    </div>
                                                    <button  onClick={() => handleRemoveCustomExpense(exp.id)}>
                                                        <svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M15.8925 17.0357L16.8925 17.0446L15.8925 17.0357ZM1 4C0.447715 4 0 4.44772 0 5C0 5.55228 0.447715 6 1 6V4ZM17 6C17.5523 6 18 5.55228 18 5C18 4.44772 17.5523 4 17 4V6ZM8 9C8 8.44772 7.55228 8 7 8C6.44772 8 6 8.44772 6 9H8ZM6 17C6 17.5523 6.44772 18 7 18C7.55228 18 8 17.5523 8 17H6ZM12 9C12 8.44772 11.5523 8 11 8C10.4477 8 10 8.44772 10 9H12ZM10 17C10 17.5523 10.4477 18 11 18C11.5523 18 12 17.5523 12 17H10ZM16 5L15 4.99107L14.8926 17.0268L15.8925 17.0357L16.8925 17.0446L17 5.00893L16 5ZM11.8927 21V20H6V21V22H11.8927V21ZM2 5H1V17H2H3V5H2ZM1 5V6H2V5V4H1V5ZM2 5V6H5V5V4H2V5ZM5 5V6H13V5V4H5V5ZM13 5V6H16V5V4H13V5ZM16 5V6H17V5V4H16V5ZM5 4.55556H6C6 3.25163 7.22916 2 9 2V1V0C6.35256 0 4 1.93212 4 4.55556H5ZM9 1V2C10.7708 2 12 3.25163 12 4.55556H13H14C14 1.93212 11.6474 0 9 0V1ZM5 4.55556H4V5H5H6V4.55556H5ZM13 4.55556H12V5H13H14V4.55556H13ZM6 21V20C4.34314 20 3 18.6569 3 17H2H1C1 19.7614 3.23857 22 6 22V21ZM15.8925 17.0357L14.8926 17.0268C14.8779 18.6731 13.5391 20 11.8927 20V21V22C14.6367 22 16.868 19.7885 16.8925 17.0446L15.8925 17.0357ZM7 9H6V17H7H8V9H7ZM11 9H10V17H11H12V9H11Z" fill="#414651" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-between justify-content-between w-100 ">
                                            <div className='ml-0'>
                                                <CheckboxWithLabel
                                                    label={t('IS_MILEAGE_TAX_FREE')}
                                                    tooltipId={name + '-mileage-taxfree-main'}
                                                    tooltipActiveId={activeTooltipId}
                                                    setTooltipActiveId={setActiveTooltipId}
                                                    checked={exp.isTaxFreeArticle}
                                                    onChange={() => setIsMileageTaxFree(!isMileageTaxFree)}
                                                    tooltipLabel="WORK_RELATED_TRAVEL_EXPENSES_REIMBURSEMENT"
                                                    isTooltip={false}
                                                />
                                            </div>
                                            <div>
                                                <CheckboxWithLabel
                                                    label={t('SHOW_MILEAGE_ON_INVOICE')}
                                                    tooltipId={name + '-mileage-showoninvoice-main'}
                                                    tooltipActiveId={activeTooltipId}
                                                    setTooltipActiveId={setActiveTooltipId}
                                                    checked={exp.isTaxFreeArticle}
                                                    onChange={() => setShowMileageOnInvoice(!showMileageOnInvoice)}
                                                    tooltipLabel="SHOW_MILEAGE_ON_INVOICE_TOOLTIP"
                                                    isTooltip={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            case 'travel':
                                return (
                                    <div key={exp.id}>
                                        <div className="form-group border-0 d-flex mb-3 w-100">
                                            <div className='d-flex justify-content-between gap-3 '>
                                                <div className='d-flex flex-column gap-2 mb-0'>
                                                    <div className='d-flex gap-0'>
                                                        <AmountInput
                                                            label={t('TRAVEL_EXPENSES')}
                                                            value={exp.amount}
                                                            placeholder='0'
                                                            currency={currency}
                                                            onChange={e => handleCustomExpenseChange(exp.id, 'amount', Number(e.target.value))}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="w-50">
                                                    <FormField
                                                        type="select"
                                                        label={t('VAT')}
                                                        name="vat"
                                                        value={exp.vat != null ? String(exp.vat) : '0'}
                                                        placeholder="Select VAT"
                                                        activeTooltipId={activeTooltipId}
                                                        setActiveTooltipId={setActiveTooltipId}
                                                        isNote={false}
                                                        options={VAT_OPTIONS.map(v => ({ label: `${v} %`, value: v }))}
                                                        onChange={e => handleCustomExpenseChange(exp.id, 'vat', e.target.value === '' ? '' : Number(e.target.value))}
                                                        expensesId={true}
                                                    />
                                                </div>
                                                <button className='mt-3' onClick={() => handleRemoveCustomExpense(exp.id)}>
                                                    <svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M15.8925 17.0357L16.8925 17.0446L15.8925 17.0357ZM1 4C0.447715 4 0 4.44772 0 5C0 5.55228 0.447715 6 1 6V4ZM17 6C17.5523 6 18 5.55228 18 5C18 4.44772 17.5523 4 17 4V6ZM8 9C8 8.44772 7.55228 8 7 8C6.44772 8 6 8.44772 6 9H8ZM6 17C6 17.5523 6.44772 18 7 18C7.55228 18 8 17.5523 8 17H6ZM12 9C12 8.44772 11.5523 8 11 8C10.4477 8 10 8.44772 10 9H12ZM10 17C10 17.5523 10.4477 18 11 18C11.5523 18 12 17.5523 12 17H10ZM16 5L15 4.99107L14.8926 17.0268L15.8925 17.0357L16.8925 17.0446L17 5.00893L16 5ZM11.8927 21V20H6V21V22H11.8927V21ZM2 5H1V17H2H3V5H2ZM1 5V6H2V5V4H1V5ZM2 5V6H5V5V4H2V5ZM5 5V6H13V5V4H5V5ZM13 5V6H16V5V4H13V5ZM16 5V6H17V5V4H16V5ZM5 4.55556H6C6 3.25163 7.22916 2 9 2V1V0C6.35256 0 4 1.93212 4 4.55556H5ZM9 1V2C10.7708 2 12 3.25163 12 4.55556H13H14C14 1.93212 11.6474 0 9 0V1ZM5 4.55556H4V5H5H6V4.55556H5ZM13 4.55556H12V5H13H14V4.55556H13ZM6 21V20C4.34314 20 3 18.6569 3 17H2H1C1 19.7614 3.23857 22 6 22V21ZM15.8925 17.0357L14.8926 17.0268C14.8779 18.6731 13.5391 20 11.8927 20V21V22C14.6367 22 16.868 19.7885 16.8925 17.0446L15.8925 17.0357ZM7 9H6V17H7H8V9H7ZM11 9H10V17H11H12V9H11Z" fill="#414651" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <div className='ml-0 mt-2 mt-md-0'>
                                                <CheckboxWithLabel
                                                    label={t('SHOW_TRAVEL_EXPENSES_ON_INVOICE')}
                                                    tooltipId={name + '-materials-taxfree-' + exp.id}
                                                    tooltipActiveId={activeTooltipId}
                                                    setTooltipActiveId={setActiveTooltipId}
                                                    checked={!!exp.isTaxFreeArticle}
                                                    onChange={() => handleCustomExpenseChange(exp.id, 'isTaxFreeArticle', !exp.isTaxFreeArticle)}
                                                    tooltipLabel="EXPENSES_FOR_MATERIALS_AND_TOOLS_TAXFREE"
                                                    isTooltip={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            case 'materials':
                                return (
                                    <div key={exp.id}>
                                        <div className="form-group border-0 d-flex mb-3 w-100">
                                            <div className='d-flex justify-content-between gap-3 '>
                                                <div className='d-flex flex-column gap-2 mb-0'>
                                                    <div className='d-flex gap-0'>
                                                        <AmountInput
                                                            label={isMobile ? t('EXPENSES_FOR_MATERIALS_AND_TOOLS_MOBILE') : t('EXPENSES_FOR_MATERIALS_AND_TOOLS')}
                                                            value={exp.amount || 0}
                                                            currency={currency}
                                                            onChange={e => handleCustomExpenseChange(exp.id, 'amount', Number(e.target.value))}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="w-50">
                                                    <FormField
                                                        type="select"
                                                        label={t('VAT')}
                                                        name="vat"
                                                        value={exp.vat != null ? String(exp.vat) : '0'}
                                                        placeholder='Select VAT'
                                                        activeTooltipId={activeTooltipId}
                                                        setActiveTooltipId={setActiveTooltipId}
                                                        isNote={false}
                                                        expensesId={true}
                                                        options={VAT_OPTIONS.map(v => ({ label: `${v} %`, value: v }))}
                                                        onChange={e => handleCustomExpenseChange(exp.id, 'vat', Number(e.target.value))}
                                                    />
                                                </div>
                                                <button className='mt-3' onClick={() => handleRemoveCustomExpense(exp.id)}>
                                                    <svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M15.8925 17.0357L16.8925 17.0446L15.8925 17.0357ZM1 4C0.447715 4 0 4.44772 0 5C0 5.55228 0.447715 6 1 6V4ZM17 6C17.5523 6 18 5.55228 18 5C18 4.44772 17.5523 4 17 4V6ZM8 9C8 8.44772 7.55228 8 7 8C6.44772 8 6 8.44772 6 9H8ZM6 17C6 17.5523 6.44772 18 7 18C7.55228 18 8 17.5523 8 17H6ZM12 9C12 8.44772 11.5523 8 11 8C10.4477 8 10 8.44772 10 9H12ZM10 17C10 17.5523 10.4477 18 11 18C11.5523 18 12 17.5523 12 17H10ZM16 5L15 4.99107L14.8926 17.0268L15.8925 17.0357L16.8925 17.0446L17 5.00893L16 5ZM11.8927 21V20H6V21V22H11.8927V21ZM2 5H1V17H2H3V5H2ZM1 5V6H2V5V4H1V5ZM2 5V6H5V5V4H2V5ZM5 5V6H13V5V4H5V5ZM13 5V6H16V5V4H13V5ZM16 5V6H17V5V4H16V5ZM5 4.55556H6C6 3.25163 7.22916 2 9 2V1V0C6.35256 0 4 1.93212 4 4.55556H5ZM9 1V2C10.7708 2 12 3.25163 12 4.55556H13H14C14 1.93212 11.6474 0 9 0V1ZM5 4.55556H4V5H5H6V4.55556H5ZM13 4.55556H12V5H13H14V4.55556H13ZM6 21V20C4.34314 20 3 18.6569 3 17H2H1C1 19.7614 3.23857 22 6 22V21ZM15.8925 17.0357L14.8926 17.0268C14.8779 18.6731 13.5391 20 11.8927 20V21V22C14.6367 22 16.868 19.7885 16.8925 17.0446L15.8925 17.0357ZM7 9H6V17H7H8V9H7ZM11 9H10V17H11H12V9H11Z" fill="#414651" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <div className='ml-0'>
                                                <div className='d-flex flex-column flex-md-row justify-content-between gap-2 gap-md-0'>
                                                    <div>
                                                        <CheckboxWithLabel
                                                            label={t('IS_MATERIALS_AND_TOOLS_TAX_FREE')}
                                                            tooltipId={name + '-materials-taxfree-' + exp.id}
                                                            tooltipActiveId={activeTooltipId}
                                                            setTooltipActiveId={setActiveTooltipId}
                                                            checked={!!exp.isTaxFreeArticle}
                                                            onChange={() => handleCustomExpenseChange(exp.id, 'isTaxFreeArticle', !exp.isTaxFreeArticle)}
                                                            tooltipLabel="EXPENSES_FOR_MATERIALS_AND_TOOLS_TAXFREE"
                                                            isTooltip={false}

                                                        />
                                                    </div>
                                                    <div>
                                                        <CheckboxWithLabel
                                                            label={t('SHOW_MATERIALS_AND_TOOLS_ON_INVOICE')}
                                                            tooltipId={name + '-materials-showoninvoice-' + exp.id}
                                                            tooltipActiveId={activeTooltipId}
                                                            setTooltipActiveId={setActiveTooltipId}
                                                            checked={!!exp.isArticleShownInInvoice}
                                                            isTooltip={false}
                                                            onChange={() => handleCustomExpenseChange(exp.id, 'isArticleShownInInvoice', !exp.isArticleShownInInvoice)}
                                                            tooltipLabel="SHOW_MATERIALS_AND_TOOLS_ON_INVOICE_TOOLTIP"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            case 'allowance_sweden':
                                return (
                                    <div key={exp.id} className='my-3'>
                                        <div className='d-flex align-items-center pt-2'>
                                            <div className="flex-grow-1">
                                                <AllowanceSection
                                                    label={t('ALLOWANCE_IN_SWEDEN')}
                                                    value={exp.amount}
                                                    onChange={e => handleCustomExpenseChange(exp.id, 'amount', Number(e.target.value))}
                                                    currency={currency}
                                                    tooltipActiveId={activeTooltipId}
                                                    setTooltipActiveId={setActiveTooltipId}
                                                    name={`${name}-sweden-${exp.id}`}
                                                    checked={!!exp.isTaxFreeArticle}
                                                    isShowTooltip={false}
                                                />
                                            </div>

                                            <button className='mt-3' onClick={() => handleRemoveCustomExpense(exp.id)}>
                                                <svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M15.8925 17.0357L16.8925 17.0446L15.8925 17.0357ZM1 4C0.447715 4 0 4.44772 0 5C0 5.55228 0.447715 6 1 6V4ZM17 6C17.5523 6 18 5.55228 18 5C18 4.44772 17.5523 4 17 4V6ZM8 9C8 8.44772 7.55228 8 7 8C6.44772 8 6 8.44772 6 9H8ZM6 17C6 17.5523 6.44772 18 7 18C7.55228 18 8 17.5523 8 17H6ZM12 9C12 8.44772 11.5523 8 11 8C10.4477 8 10 8.44772 10 9H12ZM10 17C10 17.5523 10.4477 18 11 18C11.5523 18 12 17.5523 12 17H10ZM16 5L15 4.99107L14.8926 17.0268L15.8925 17.0357L16.8925 17.0446L17 5.00893L16 5ZM11.8927 21V20H6V21V22H11.8927V21ZM2 5H1V17H2H3V5H2ZM1 5V6H2V5V4H1V5ZM2 5V6H5V5V4H2V5ZM5 5V6H13V5V4H5V5ZM13 5V6H16V5V4H13V5ZM16 5V6H17V5V4H16V5ZM5 4.55556H6C6 3.25163 7.22916 2 9 2V1V0C6.35256 0 4 1.93212 4 4.55556H5ZM9 1V2C10.7708 2 12 3.25163 12 4.55556H13H14C14 1.93212 11.6474 0 9 0V1ZM5 4.55556H4V5H5H6V4.55556H5ZM13 4.55556H12V5H13H14V4.55556H13ZM6 21V20C4.34314 20 3 18.6569 3 17H2H1C1 19.7614 3.23857 22 6 22V21ZM15.8925 17.0357L14.8926 17.0268C14.8779 18.6731 13.5391 20 11.8927 20V21V22C14.6367 22 16.868 19.7885 16.8925 17.0446L15.8925 17.0357ZM7 9H6V17H7H8V9H7ZM11 9H10V17H11H12V9H11Z" fill="#414651" />
                                                </svg>
                                            </button>
                                        </div>
                                        <div>
                                            <div className='d-flex align-items-between justify-content-between mb-0'>
                                                <div>
                                                    <CheckboxWithLabel
                                                        label={t('IS_ALLOWANCE_IN_SWEDEN_TAX_FREE')}
                                                        tooltipId={name + '-sweden-taxfree-' + exp.id}
                                                        checked={!!exp.isTaxFreeArticle}
                                                        onChange={() => handleCustomExpenseChange(exp.id, 'isTaxFreeArticle', !exp.isTaxFreeArticle)}
                                                        tooltipActiveId={activeTooltipId}
                                                        setTooltipActiveId={setActiveTooltipId}
                                                        tooltipLabel="ALLOWANCE_IN_SWEDEN_TAXFREE"
                                                        isTooltip={false}
                                                    />
                                                </div>
                                                <div>
                                                    <CheckboxWithLabel
                                                        label={t('SHOW_ALLOWANCE_IN_SWEDEN_ON_INVOICE')}
                                                        tooltipId={name + '-sweden-showoninvoice-' + exp.id}
                                                        checked={!!exp.isArticleShownInInvoice}
                                                        onChange={() => handleCustomExpenseChange(exp.id, 'isArticleShownInInvoice', !exp.isArticleShownInInvoice)}
                                                        tooltipActiveId={activeTooltipId}
                                                        setTooltipActiveId={setActiveTooltipId}
                                                        isTooltip={false}
                                                        tooltipLabel="SHOW_ALLOWANCE_IN_SWEDEN_ON_INVOICE_TOOLTIP"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            case 'allowance_outside_sweden':
                                return (
                                    <div key={exp.id}>
                                        <div className='d-flex align-items-center pt-2'>
                                            <div className="flex-grow-1">
                                                <AllowanceSection
                                                    label={t('ALLOWANCE_OUTSIDE_SWEDEN')}
                                                    value={exp.amount}
                                                    onChange={e => handleCustomExpenseChange(exp.id, 'amount', Number(e.target.value))}
                                                    currency={currency}
                                                    tooltipActiveId={activeTooltipId}
                                                    setTooltipActiveId={setActiveTooltipId}
                                                    name={`${name}-outside-sweden-${exp.id}`}
                                                    checked={!!exp.isTaxFreeArticle}
                                                    isShowTooltip={false}
                                                />
                                            </div>

                                            <button className='mt-3' onClick={() => handleRemoveCustomExpense(exp.id)}>
                                                <svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M15.8925 17.0357L16.8925 17.0446L15.8925 17.0357ZM1 4C0.447715 4 0 4.44772 0 5C0 5.55228 0.447715 6 1 6V4ZM17 6C17.5523 6 18 5.55228 18 5C18 4.44772 17.5523 4 17 4V6ZM8 9C8 8.44772 7.55228 8 7 8C6.44772 8 6 8.44772 6 9H8ZM6 17C6 17.5523 6.44772 18 7 18C7.55228 18 8 17.5523 8 17H6ZM12 9C12 8.44772 11.5523 8 11 8C10.4477 8 10 8.44772 10 9H12ZM10 17C10 17.5523 10.4477 18 11 18C11.5523 18 12 17.5523 12 17H10ZM16 5L15 4.99107L14.8926 17.0268L15.8925 17.0357L16.8925 17.0446L17 5.00893L16 5ZM11.8927 21V20H6V21V22H11.8927V21ZM2 5H1V17H2H3V5H2ZM1 5V6H2V5V4H1V5ZM2 5V6H5V5V4H2V5ZM5 5V6H13V5V4H5V5ZM13 5V6H16V5V4H13V5ZM16 5V6H17V5V4H16V5ZM5 4.55556H6C6 3.25163 7.22916 2 9 2V1V0C6.35256 0 4 1.93212 4 4.55556H5ZM9 1V2C10.7708 2 12 3.25163 12 4.55556H13H14C14 1.93212 11.6474 0 9 0V1ZM5 4.55556H4V5H5H6V4.55556H5ZM13 4.55556H12V5H13H14V4.55556H13ZM6 21V20C4.34314 20 3 18.6569 3 17H2H1C1 19.7614 3.23857 22 6 22V21ZM15.8925 17.0357L14.8926 17.0268C14.8779 18.6731 13.5391 20 11.8927 20V21V22C14.6367 22 16.868 19.7885 16.8925 17.0446L15.8925 17.0357ZM7 9H6V17H7H8V9H7ZM11 9H10V17H11H12V9H11Z" fill="#414651" />
                                                </svg>
                                            </button>
                                        </div>


                                        <div className=''>
                                            <div className='d-flex align-items-between justify-content-between my-2'>
                                                <div>
                                                    <CheckboxWithLabel
                                                        label={t('IS_ALLOWANCE_OUTSIDE_SWEDEN_TAX_FREE')}
                                                        tooltipId={name + '-outside-taxfree-' + exp.id}
                                                        checked={!!exp.isTaxFreeArticle}
                                                        onChange={() => handleCustomExpenseChange(exp.id, 'isTaxFreeArticle', !exp.isTaxFreeArticle)}
                                                        tooltipActiveId={activeTooltipId}
                                                        setTooltipActiveId={setActiveTooltipId}
                                                        tooltipLabel="ALLOWANCE_OUTSIDE_SWEDEN_TAXFREE"
                                                        isTooltip={false}

                                                    />
                                                </div>
                                                <div>
                                                    <CheckboxWithLabel
                                                        label={t('SHOW_ALLOWANCE_OUTSIDE_SWEDEN_ON_INVOICE')}
                                                        tooltipId={name + '-sweden-showoninvoice-' + exp.id}
                                                        checked={!!exp.isArticleShownInInvoice}
                                                        onChange={() => handleCustomExpenseChange(exp.id, 'isArticleShownInInvoice', !exp.isArticleShownInInvoice)}
                                                        tooltipActiveId={activeTooltipId}
                                                        setTooltipActiveId={setActiveTooltipId}
                                                        isTooltip={false}
                                                        tooltipLabel="SHOW_ALLOWANCE_IN_SWEDEN_ON_INVOICE_TOOLTIP"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            default:
                                return null;
                        }
                    })}
                </div>
                <div className="dropdown w-100 position-relative my-3">
                    <button
                        className="btn mb-4 btn-mileage d-flex align-items-center justify-content-center gap-2 w-100 "
                        type="button"
                        id="mileageDropdown"
                        onClick={() => setIsDropdownOpen(prev => !prev)}
                        aria-expanded={isDropdownOpen}
                    >
                        <img src={Plus} alt="" className="me-2" />
                        <span className='mx-2 '>{t('ADD_EXPENSES')}</span>
                        <svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                            <path d="M11 1.33331L6.58926 5.74406C6.26382 6.0695 5.73618 6.0695 5.41074 5.74406L1 1.33331" stroke="#181D27" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                    <div
                        className={`dropdown-menu w-100 position-absolute top-100 left-0 mt-1 ${isDropdownOpen ? 'show' : ''}`}
                        aria-labelledby="mileageDropdown">
                        {ALLEXPENSESDATA.map(({ type, label }) => (
                            <button
                                key={type}
                                className="dropdown-item p-2"
                                type="button"
                                onClick={() => { handleAddCustomExpense(type as CustomExpenseType); setIsDropdownOpen(false); }}
                            >
                                {t(label)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

export default InvoiceAdvancedCalculator;
